import { Box, CircularProgress } from '@mui/material';
import BackendService from 'BackendService';
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const IMP_LOG = '[impersonation:web]';

/** Safe for console — one-time consume code, not Auth0 secrets. */
function codeHint(code: string | null): string {
  if (!code) return '(none)';
  const t = code.trim();
  return t.length <= 10 ? `${t.slice(0, 4)}…` : `${t.slice(0, 8)}…`;
}

/**
 * After consume the backend sets imp_ctx (httpOnly cookie). Poll
 * /impersonations/status — which now accepts the cookie as sole auth
 * credential (Stage 0.8) — until it confirms impersonating=true.
 */
async function waitForImpersonationStatusReady(svc: BackendService): Promise<void> {
  const max = 12;
  const delayMs = 150;
  for (let attempt = 1; attempt <= max; attempt++) {
    try {
      const status = await svc.getImpersonationStatus();
      if (status.impersonating === true) {
        console.info(IMP_LOG, 'impersonations/status confirmed impersonating', {
          attempt,
        });
        return;
      }
      console.info(IMP_LOG, 'impersonations/status not yet impersonating', {
        attempt,
        impersonating: status.impersonating,
      });
    } catch (e) {
      console.warn(IMP_LOG, 'getImpersonationStatus failed', { attempt, e });
    }
    if (attempt < max) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  console.warn(IMP_LOG, 'impersonations/status never showed impersonating; navigating anyway', {
    maxAttempts: max,
  });
}

const Impersonate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasConsumedRef = useRef(false);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    const code = new URLSearchParams(location.search).get('code');

    // Prevent duplicate calls
    if (hasConsumedRef.current || isProcessingRef.current) {
      return;
    }

    if (!code) {
      console.warn(IMP_LOG, 'no consume code in URL', {
        search: location.search,
      });
      navigate('/');
      return;
    }

    isProcessingRef.current = true;

    console.info(IMP_LOG, 'starting consume', {
      codeHint: codeHint(code),
      href: window.location.href,
    });

    // After the backend sets imp_ctx, poll /impersonations/status (cookie-only
    // auth — no Auth0 Bearer required) then navigate home. CheckLoginComponent
    // will detect the impersonation cookie and render without triggering an
    // Auth0 login redirect.
    const proceedAfterConsume = async () => {
      await waitForImpersonationStatusReady(new BackendService());
      console.info(IMP_LOG, 'navigate to / (imp_ctx confirmed)');
      navigate('/');
    };

    new BackendService()
      .consumeImpersonation(code)
      .then(async () => {
        hasConsumedRef.current = true;
        console.info(IMP_LOG, 'consume promise resolved');
        await proceedAfterConsume();
      })
      .catch((error) => {
        console.error(IMP_LOG, 'consume failed', error);

        // If the code was already consumed, still try to proceed — the
        // imp_ctx cookie may already be set from a prior consume.
        const errorMessage = (error?.message ?? '') + (error?.error?.message ?? '');
        const isAlreadyConsumed =
          errorMessage.includes('already') ||
          errorMessage.includes('consumed') ||
          errorMessage.includes('invalid') ||
          errorMessage.includes('expired');

        if (isAlreadyConsumed) {
          console.warn(IMP_LOG, 'treating as already consumed — proceedAfterConsume');
          proceedAfterConsume().catch(() => window.location.replace('/'));
        } else {
          window.location.replace('/');
        }
      });
  }, [location, navigate]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );
};

export default Impersonate;
